import { useEffect, useMemo, useState } from 'react';

import useTranslation from '@hooks/useTranslation';
import { useUI } from '@hooks/useUI';
import {
    Box,
    Button,
    Checkbox,
    Drawer,
    FormControlLabel,
    IconButton,
    Paper,
    SxProps,
    Typography,
} from '@mui/material';
import { GridFilterListIcon } from '@mui/x-data-grid';

export type CherryPickHadithPicker<T> = {
    /**
     * The unique identifier for the picker, it should match the key in the hadith object
     */
    key: string;
    /**
     * The key you want to be returned on submit instead of the picker `key`
     */
    job_key?: string;
    /**
     * The picker label
     */
    label: string;
    /**
     * Whether the picker is disabled
     */
    disabled?: boolean;
    /**
     * used for special pickers that are not related to the hadith object, the hoax will not be returned on submit
     */
    hoax?: boolean;
    /**
     * The row that the picker will be placed in
     */
    row: number;
    /**
     * The style that will be applied to the picker
     * @param pickers - the row and all the pickers in that row
     */
    style?: (
        rowPickers: CherryPickHadithPickerClone<T>[],
        allPickers: CherryPickHadithPickerClone<T>[],
    ) => SxProps;
    /**
     * The content that will be displayed if `picked=true`
     */
    content?: (
        hadith: T,
        /**
         * The state pickers (related to the current picker)
         */
        relatedPickers: CherryPickHadithPickerClone<T>[],
    ) => React.ReactNode;
    /**
     * The content that will be displayed if `picked=false`, and no related pickers are picked
     */
    subContent?: (
        hadith: T,
        /**
         * The state pickers (related to the current picker)
         */
        relatedPickers: CherryPickHadithPickerClone<T>[],
    ) => React.ReactNode;
    /**
     * controls the visibility of the picker and its related items (e.g. content, subContent, etc.)
     */
    condition?: (currentPicker: CherryPickHadithPickerClone<T>, pickers: CherryPickHadithPickerClone<T>[]) => boolean;
    /**
     * Relation is a way to control the content of another pickers remotely
     */
    relation?: string | string[];
    /**
     * defines the text of the submit button
     */
    submitText?: string;
    /**
     * defines the function that will be called when the submit button is clicked
     */
    onSubmit?: (pickersPicked: Record<string, boolean>) => void;
};

type CherryPickHadithPickerClone<T> = CherryPickHadithPicker<T> & {
    picked: boolean;
    hadith: T
};

export type CherryPickHadithContentProps<T> = {
    hadith: T;
    pickers: CherryPickHadithPicker<T>[];
    children?: React.ReactNode;
    submitText?: string;
    onSubmit?: (pickersPicked: Record<string, boolean>) => void;
};
export function CherryPickHadithContent<T>({
    hadith,
    pickers,
    children,
    submitText,
    onSubmit,
}: CherryPickHadithContentProps<T>) {
    const { t: tMic } = useTranslation('mics');
    const { isMobileOrTablet } = useUI();

    const [openPickerDrawer, setOpenPickerDrawer] = useState(false);

    const [pickersClone, setPickersClone] = useState<
        CherryPickHadithPickerClone<T>[]
    >([]);

    const pickedContent = useMemo(() => {
        return pickersClone
            .filter((picker) => {
                if (!picker.content && !picker.subContent) {
                    return false;
                }

                const condition = picker.condition?.(picker, pickersClone) ?? true;

                return (
                    (picker.subContent && condition) ||
                    (picker.picked && condition)
                );
            })
            .reduce((acc, picker) => {
                if (!acc[picker.row]) {
                    acc[picker.row] = [];
                }

                acc[picker.row].push(picker);
                return acc;
            }, {} as Record<number, CherryPickHadithPickerClone<T>[]>); // [1, [picker1, picker2]]
    }, [pickersClone]);

    useEffect(() => {
        const pickersClone: CherryPickHadithPickerClone<T>[] = pickers.map((picker) => ({
            ...picker,
            picked: !picker.disabled,
            hadith,
        })).filter((picker, _, pickers) => picker.condition?.(picker, pickers) ?? true);

        setPickersClone(pickersClone);
    }, [pickers]);

    const pickerPicked = useMemo(() => {
        return pickersClone.reduce((acc, picker) => {
            if (!picker.hoax) {
                acc[picker.job_key ?? picker.key] = picker.picked;
            }
            return acc;
        }, {} as Record<string, boolean>);
    }, [pickersClone]);

    function renderPickerContent(picker: CherryPickHadithPickerClone<T>) {
        if (picker.relation?.length) {
            const relatedPickers = pickersClone.filter((p) =>
                picker?.relation?.includes(p.key),
            );

            return picker.picked
                ? picker.content?.(hadith, relatedPickers)
                : picker.subContent?.(hadith, relatedPickers);
        }

        return picker.picked
            ? picker.content?.(hadith, [])
            : picker.subContent?.(hadith, []);
    }

    return (
        <>
            <Box sx={{ maxHeight: '100%', overflow: 'auto' }}>
                {children}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        '@media (max-width: 768px)': {
                            flexDirection: 'column',
                            gap: 2,
                        },
                    }}
                >
                    {isMobileOrTablet ? (
                        <Box>
                            <IconButton
                                onClick={() => setOpenPickerDrawer(true)}
                            >
                                <GridFilterListIcon />
                            </IconButton>
                        </Box>
                    ) : (
                        <Pickers<T>
                            pickersClone={pickersClone}
                            setPickersClone={setPickersClone}
                        />
                    )}
                    <Paper
                        elevation={0}
                        variant="outlined"
                        sx={{
                            p: 2,
                            flexGrow: 1,
                        }}
                    >
                        {Object.entries(pickedContent).map(([row, pickers]) => (
                            <Box
                                key={row}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                {pickers.map((picker) => (
                                    <Box
                                        sx={{
                                            ...(picker.style?.(
                                                pickers,
                                                pickersClone,
                                            ) ?? {}),
                                        }}
                                        key={picker.key}
                                    >
                                        {renderPickerContent(picker)}
                                    </Box>
                                ))}
                            </Box>
                        ))}
                    </Paper>
                </Box>
                <Box
                    sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}
                >
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{
                            color: 'white',
                        }}
                        onClick={() => {
                            onSubmit?.(pickerPicked);
                        }}
                    >
                        {submitText ?? tMic('submit')}
                    </Button>
                </Box>
            </Box>
            <Drawer
                anchor="bottom"
                open={openPickerDrawer}
                onClose={() => setOpenPickerDrawer(false)}
                sx={{ zIndex: 9999 }}
            >
                <Pickers<T>
                    pickersClone={pickersClone}
                    setPickersClone={setPickersClone}
                />
                <Box sx={{ py: 2 }}></Box>
            </Drawer>
        </>
    );
}

function Pickers<T>({
    pickersClone,
    setPickersClone,
}: {
    pickersClone: CherryPickHadithPickerClone<T>[];
    setPickersClone: React.Dispatch<
        React.SetStateAction<CherryPickHadithPickerClone<T>[]>
    >;
}) {
    return (
        <Box
            sx={{
                px: 2,
                flexShrink: 0,
                maxWidth: '100%',
                '@media (min-width: 769px)': {
                    maxWidth: '300px',
                },
            }}
        >
            {pickersClone.map(
                (picker) =>
                    !picker.hoax && (
                        <FormControlLabel
                            sx={{
                                display: 'flex',
                                maxWidth: '100%',
                            }}
                            key={picker.key}
                            label={picker.label}
                            disabled={picker.disabled}
                            control={
                                <Checkbox
                                    color="secondary"
                                    size="small"
                                    checked={picker.picked}
                                    onChange={(event) => {
                                        setPickersClone((prev) =>
                                            prev.map((p) =>
                                                p.key === picker.key
                                                    ? {
                                                        ...p,
                                                        picked: event.target
                                                            .checked,
                                                    }
                                                    : p,
                                            ),
                                        );
                                    }}
                                />
                            }
                        />
                    ),
            )}
        </Box>
    );
}

/**
 * ***********************
 * * Relations Explained *
 * ***********************
 *
 * Base relation: a relation that doesn't rely on any other relations,
 *                and its content is affected by the state of the State relations attached to it,
 *                *the base relation can watch on multiple state relations*
 *
 * State relation: a relation that only acts as a state with no gived content,
 *                 the change of its state will affect the base relation attached to it
 *
 *
 *                  ----- BASE RELATION -----
 *              |                                |
 *  ----- STATE RELATION -----       ----- STATE RELATION -----
 *
 *
 */
